/* Wedge product-side PostHog instrumentation (2026-07-12, boardroom
 * wf_39b8fb6b workstream 4).
 *
 * Design constraints from the board ruling:
 *  - SDK lazy-loads AFTER first paint (field reps on LTE · no LCP tax).
 *    Events fired before the SDK lands queue in-memory and flush on load.
 *  - tenant + tier + surface + build stamp ride on EVERY event, because
 *    live white-label tenants exist and funnels must segment per-tenant.
 *  - Same PostHog project as the marketing site (436840) as the
 *    board-approved stopgap: the free plan caps the org at one project.
 *    `surface: 'wedge'` discriminates every event; a dedicated wedge
 *    project needs Luke's billing call (see session notes 2026-07-12).
 *  - The token below is the project's WRITE-ONLY client key. PostHog
 *    labels it "Safe to use in public apps" and it is already public in
 *    the served marketing bundle.
 *  - autocapture stays OFF: the wedge ships deliberate product events
 *    only (brief_requested / brief_generated / brief_failed / cap_hit),
 *    not DOM noise from a 21k-line prototype bundle.
 *
 * The prototype layer (window globals, no imports) reaches this through
 * window.__WEDGE_TRACK__, installed by initWedgeAnalytics().
 */

const POSTHOG_KEY = 'phc_zz6cEwuCVcn6KHB6yx2mnjnkVhFgJaSMAt5kQecJNk5n';
const POSTHOG_HOST = 'https://us.i.posthog.com';

type Props = Record<string, string | number | boolean | null | undefined>;

interface PosthogLike {
  init: (key: string, opts: Record<string, unknown>) => void;
  capture: (event: string, props?: Record<string, unknown>) => void;
  identify: (id: string, props?: Record<string, unknown>) => void;
  register: (props: Record<string, unknown>) => void;
}

let client: PosthogLike | null = null;
let loading = false;
const queue: Array<{ event: string; props?: Props }> = [];
let pendingIdentify: { id: string; props?: Props } | null = null;
let superProps: Props = {};

function flush(): void {
  if (!client) return;
  try {
    client.register({ ...superProps });
    if (pendingIdentify) {
      client.identify(pendingIdentify.id, pendingIdentify.props);
      pendingIdentify = null;
    }
    while (queue.length) {
      const q = queue.shift();
      if (q) client.capture(q.event, q.props);
    }
  } catch (e) { /* analytics must never break the product */ }
}

function loadSdk(): void {
  if (client || loading || typeof window === 'undefined') return;
  loading = true;
  import('posthog-js')
    .then((mod) => {
      const ph = (mod.default ?? mod) as unknown as PosthogLike;
      ph.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        person_profiles: 'identified_only',
      });
      client = ph;
      flush();
    })
    .catch(() => { loading = false; /* offline / blocked · retry on next event */ });
}

/** Update the properties stamped onto every subsequent event. Call again
 *  whenever tenant/tier resolve or change · last write wins. */
export function setWedgeAnalyticsContext(ctx: {
  tenant?: string | null;
  tier?: string | null;
  userId?: string | null;
  email?: string | null;
}): void {
  superProps = {
    ...superProps,
    surface: 'wedge',
    build: (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).__WEDGE_BUILD__ as string) || 'unknown',
    ...(ctx.tenant !== undefined ? { tenant: ctx.tenant ?? 'unknown' } : {}),
    ...(ctx.tier !== undefined ? { tier: ctx.tier ?? 'unknown' } : {}),
  };
  if (ctx.userId) {
    pendingIdentify = { id: ctx.userId, props: ctx.email ? { email: ctx.email } : undefined };
  }
  flush();
}

/** Fire a product event. Queues until the lazily-loaded SDK is ready. */
export function wedgeTrack(event: string, props?: Props): void {
  try {
    queue.push({ event, props: { ...superProps, ...props } });
    if (queue.length > 100) queue.shift(); // bound the offline queue
    if (client) flush();
    else loadSdk();
  } catch (e) { /* never throw into the product */ }
}

/** Install after first paint. Exposes window.__WEDGE_TRACK__ for the
 *  prototype bundle (window-globals contract) and schedules the SDK load
 *  off the critical path. */
export function initWedgeAnalytics(): void {
  if (typeof window === 'undefined') return;
  (window as unknown as Record<string, unknown>).__WEDGE_TRACK__ = wedgeTrack;
  const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
  if (typeof idle === 'function') idle(() => loadSdk(), { timeout: 5000 });
  else setTimeout(loadSdk, 3500);
}
